import { useEffect, useState } from "react";
import { mutate } from "swr";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../../api/client";
import { toast } from "../../lib/toast";
import "../CreateProjectModal.css";

type WipeType = "ocr_flag" | "language" | "content" | "note";

const WIPE_OPTION_KEYS: WipeType[] = ["ocr_flag", "language", "content", "note"];

interface Props {
  paperId: number;
  paperName: string;
  onClose: () => void;
}

export function WipeAnnotationsModal({ paperId, paperName, onClose }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<WipeType>>(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  function toggle(key: WipeType) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const canSubmit = selected.size > 0 && confirmed && !submitting;

  async function onWipe() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/papers/${paperId}/wipe_annotations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ types: Array.from(selected) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      void mutate(`/api/papers/${paperId}/annotations`);
      void mutate(`/api/papers/${paperId}/status`);
      void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
      toast(t("wipe_modal.wiped_toast"));
      onClose();
    } catch {
      toast(t("wipe_modal.wipe_failed_toast"));
      setSubmitting(false);
    }
  }

  const OPTION_LABELS: Record<WipeType, { label: string; desc: string }> = {
    ocr_flag: { label: t("wipe_modal.option_ocr_label"), desc: t("wipe_modal.option_ocr_desc") },
    language: { label: t("wipe_modal.option_language_label"), desc: t("wipe_modal.option_language_desc") },
    content: { label: t("wipe_modal.option_content_label"), desc: t("wipe_modal.option_content_desc") },
    note: { label: t("wipe_modal.option_note_label"), desc: t("wipe_modal.option_note_desc") },
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={t("wipe_modal.title")}>
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          disabled={submitting}
          aria-label={t("common.close")}
        >
          ✕
        </button>

        <h2 className="modal-card__title">{t("wipe_modal.title")}</h2>
        <p className="wipe-paper-name">{paperName}</p>

        <div className="wipe-options">
          {WIPE_OPTION_KEYS.map((key) => {
            const { label, desc } = OPTION_LABELS[key];
            return (
              <label key={key} className="wipe-option">
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => toggle(key)}
                  disabled={submitting}
                />
                <div>
                  <div className="wipe-option-label">{label}</div>
                  <div className="wipe-option-desc">{desc}</div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="wipe-warning">{t("wipe_modal.warning")}</div>

        <label className="wipe-confirm-row">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={submitting}
          />
          {t("wipe_modal.confirm_text")}
        </label>

        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="modal-btn modal-btn--danger"
            disabled={!canSubmit}
            onClick={() => void onWipe()}
          >
            {submitting ? t("wipe_modal.wiping") : t("wipe_modal.wipe_selected_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}
